// src/features/dashboard/hooks/useTeamKpi.ts

import {
  useEffect,
  useState,
} from 'react';

import {
  ENV,
} from '../../../config/env';

import {
  getTeamKpiCards,
  teamKpiMockData,
} from '../mocks/teamKpi.mock';

import {
  getTeamKpi,
} from '../services/teamKpiApi';

import type {
  TeamKpiItem,
} from '../types/teamKpi.types';

import {
  useManagerDate,
  formatManagerDate,
} from '../context/ManagerDateContext';


/* ============================================================
   TYPES
============================================================ */

type UseTeamKpiParams = {
  ManagerEmployeeCode?: string;
};


/* ============================================================
   HOOK
============================================================ */

export const useTeamKpi = ({
  ManagerEmployeeCode,
}: UseTeamKpiParams = {}) => {

  /* ==========================================================
     MANAGER DATE CONTEXT

     This is the SAME date state used by DashboardHeader.

     When the date filter changes:

       fromDate / toDate
              ↓
       this hook re-renders
              ↓
       useEffect runs
              ↓
       Team KPI API is called again
  ========================================================== */

  const {
    fromDate,
    toDate,
  } = useManagerDate();


  /* ==========================================================
     FORMAT DATE FOR API

     IMPORTANT:

     DO NOT USE:

       fromDate.toISOString().split('T')[0]

     because it can change:

       15-Sep-2026
              ↓
       2026-09-14

     formatManagerDate() uses LOCAL date components.
  ========================================================== */

  const fromDateString =
    formatManagerDate(
      fromDate,
    );

  const toDateString =
    formatManagerDate(
      toDate,
    );


  /* ==========================================================
     STATE
  ========================================================== */

  const [
    kpiCards,
    setKpiCards,
  ] = useState<TeamKpiItem[]>([]);

  const [
    loading,
    setLoading,
  ] = useState<boolean>(false);

  const [
    error,
    setError,
  ] = useState<string>('');


  /* ==========================================================
     API
  ========================================================== */

  useEffect(() => {

    let cancelled = false;


    const loadKpi =
      async () => {

        /* ====================================================
           EMPLOYEE CODE VALIDATION
        ==================================================== */

        const managerEmployeeCode =
          String(
            ManagerEmployeeCode ?? '',
          ).trim();


        if (
          !managerEmployeeCode
        ) {

          console.warn(
            'TEAM KPI API SKIPPED: ManagerEmployeeCode is missing.',
          );

          setKpiCards([]);

          setLoading(false);

          return;
        }


        /* ====================================================
           DATE VALIDATION
        ==================================================== */

        if (
          !fromDateString ||
          !toDateString
        ) {

          console.warn(
            'TEAM KPI API SKIPPED: Date is missing.',
            {
              fromDate:
                fromDateString,

              toDate:
                toDateString,
            },
          );

          setKpiCards([]);

          setLoading(false);

          return;
        }


        /* ====================================================
           DATE RANGE VALIDATION
        ==================================================== */

        if (
          fromDateString >
          toDateString
        ) {

          console.warn(
            'TEAM KPI API SKIPPED: Invalid date range.',
            {
              fromDate:
                fromDateString,

              toDate:
                toDateString,
            },
          );

          setKpiCards([]);

          setError(
            'From date cannot be greater than To date.',
          );

          setLoading(false);

          return;
        }


        /* ====================================================
           START LOADING
        ==================================================== */

        setLoading(true);

        setError('');


        /* ====================================================
           DEBUG
        ==================================================== */

        console.log(
          '==========================================',
        );

        console.log(
          'TEAM KPI DATE FILTER',
        );

        console.log(
          'Manager Employee Code:',
          managerEmployeeCode,
        );

        console.log(
          'Selected From Date:',
          fromDate,
        );

        console.log(
          'Selected To Date:',
          toDate,
        );

        console.log(
          'API From Date:',
          fromDateString,
        );

        console.log(
          'API To Date:',
          toDateString,
        );

        console.log(
          'ENV.USE_MOCK:',
          ENV.USE_MOCK,
        );

        console.log(
          '==========================================',
        );


        try {

          /* ==================================================
             MOCK MODE
          ================================================== */

          if (
            ENV.USE_MOCK
          ) {

            console.warn(
              'TEAM KPI API NOT CALLED because ENV.USE_MOCK = true.',
            );

            if (
              !cancelled
            ) {

              setKpiCards(
                getTeamKpiCards(
                  teamKpiMockData,
                ),
              );
            }

            return;
          }


          /* ==================================================
             API PAYLOAD
          ================================================== */

          const payload = {

            ManagerEmployeeCode:
              managerEmployeeCode,

            fromDate:
              fromDateString,

            toDate:
              toDateString,
          };


          /* ==================================================
             LOG PAYLOAD
          ================================================== */

          console.log(
            'TEAM KPI API PAYLOAD:',
            payload,
          );


          /* ==================================================
             API CALL
          ================================================== */

          const response =
            await getTeamKpi(
              payload,
            );


          /* ==================================================
             IGNORE OLD REQUEST
          ================================================== */

          if (
            cancelled
          ) {
            return;
          }


          /* ==================================================
             MAP RESPONSE
          ================================================== */

          setKpiCards(
            getTeamKpiCards(
              response,
            ),
          );

        } catch (
          err
        ) {

          if (
            cancelled
          ) {
            return;
          }

          console.error(
            'TEAM KPI API ERROR:',
            err,
          );

          setError(
            'Failed to load KPI cards',
          );

          setKpiCards([]);

        } finally {

          if (
            !cancelled
          ) {

            setLoading(
              false,
            );
          }
        }
      };


    loadKpi();


    /* ========================================================
       CLEANUP

       If user changes date while previous API is still
       running, old response will not overwrite new data.
    ======================================================== */

    return () => {

      cancelled = true;
    };

  }, [
    ManagerEmployeeCode,
    fromDateString,
    toDateString,
  ]);


  /* ==========================================================
     RETURN
  ========================================================== */

  return {
    kpiCards,
    loading,
    error,
  };
};